import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Eye, Info } from "lucide-react";

import { useApiHeaders } from "@/hooks/use-api-headers";
import {
  ClassificationBadge,
  ClassificationToggleRow,
  EnableClassificationDialog,
  type ClassificationSourceCounts,
  type ClassificationStatusEntry,
} from "@/components/admin/classification-schedule";
import { API_URL, ConnectionError, fetchJson } from "@/lib/admin-api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// POS2-716: poll cadence for the selected site's status / count tiles while a
// site is active. These track minutes-scale events (imports, classification
// draining), so 10s keeps the UI responsive while cutting poll load ~3x vs the
// old 3s — on top of the per-poll work scoped down server-side.
const SITE_POLL_INTERVAL_MS = 10_000;

type ImportType = "site" | "users" | "waste";
// "partial" is frontend-only (a rollup verdict); every other value is a
// databridge_imports.status. POS2-1092 added "queued" — a job waiting for the
// sequential worker to promote it.
type ImportStatus = "not_started" | "queued" | "in_progress" | "partial" | "completed" | "error";

interface OldSiteSummary {
  site_id: number;
  name: string;
  device_count: number;
  event_count: number;
  last_event_date: string | null;
  user_count: number;
}

interface OldDeviceSummary {
  serial: string;
  name: string | null;
  last_seen_at: string | null;
}

interface OldUserSummary {
  user_id: number;
  email: string;
  full_name: string | null;
}

interface OldEventSample {
  event_id: number;
  logged_at: string;
  item_weight: number | null;
  image_url: string | null;
}

interface OldSiteDetail extends OldSiteSummary {
  devices: OldDeviceSummary[];
  users: OldUserSummary[];
  last_events: OldEventSample[];
}

interface ImportStatusEntry {
  import_type: ImportType;
  status: ImportStatus;
  started_at: string | null;
  completed_at: string | null;
  error_log: string | null;
}

type WatchRunStatus = "idle" | "ok" | "error";

interface WatchStatusEntry {
  enabled: boolean;
  apply_food_map: boolean;
  confirm_lower_tier_maps: boolean;
  last_synced_event_id: number;
  last_synced_at: string | null;
  last_run_status: WatchRunStatus;
  last_error: string | null;
  consecutive_failures: number;
}

interface FoodMapRunEntry {
  status: ImportStatus;
  started_at: string | null;
  completed_at: string | null;
  candidates: number;
  mapped: number;
}

interface SiteImportStatus {
  old_site_id: number;
  site: ImportStatusEntry;
  users: ImportStatusEntry;
  waste: ImportStatusEntry;
  watch: WatchStatusEntry;
  classification: ClassificationStatusEntry;
  // POS2-714: in-flight "Copy Old Classifications" run.
  classification_copy: FoodMapRunEntry;
}

interface SiteImportPreview {
  old_site_id: number;
  site_name: string;
  account_id: string;
  kitchen_name: string;
  device_count: number;
  device_collisions: string[];
  already_imported: boolean;
  existing_new_site_id: string | null;
}

interface SiteImportResult {
  import_id: string;
  new_site_id: string;
  new_kitchen_id: string;
  devices_created: number;
  devices_skipped: string[];
}

interface WasteImportPreview {
  old_site_id: number;
  total_events: number;
  events_with_image: number;
  estimated_image_size_mb: number;
  already_imported: number;
  devices_not_in_new_system: string[];
}

interface WasteImportTrigger {
  import_id: string;
  status: "in_progress" | "completed";
}

// POS2-1102: the enqueue endpoint answers per site, not with a single status —
// `skipped` already had a job waiting or running, `rejected` isn't importable yet.
interface EnqueueOutcome {
  old_site_id: number;
  reason: string;
}

interface EnqueueResult {
  queued: number[];
  skipped: EnqueueOutcome[];
  rejected: EnqueueOutcome[];
}

interface ApplyFoodMapResult {
  candidates: number;
  mapped: number;
  by_tier: Record<string, number>;
  by_confidence: Record<string, number>;
  confirmed: number;
  suggested: number;
  scheduled?: boolean;
}

interface Account {
  id: string;
  name: string;
  status: string;
}

const STATUS_LABEL: Record<ImportStatus, string> = {
  not_started: "Not started",
  queued: "Queued",
  in_progress: "In progress",
  partial: "Partially imported",
  completed: "Completed",
  error: "Error",
};

// POS2-699: same pills as imports, classification wording ("partial" = some
// events still unclassified with nothing actively classifying them).
const CLASSIFICATION_STATUS_LABEL: Record<ImportStatus, string> = {
  not_started: "Not started",
  queued: "Queued",
  in_progress: "In progress",
  partial: "Partial",
  completed: "Completed",
  error: "Error",
};

const STATUS_VARIANT: Record<ImportStatus, "secondary" | "default" | "destructive" | "outline"> = {
  not_started: "secondary",
  queued: "outline",
  in_progress: "default",
  partial: "outline",
  completed: "outline",
  error: "destructive",
};

// Optional className overrides on top of the variant — used for the "partial"
// amber and "queued" blue colours that no built-in Badge variant carries.
// Queued is blue, not a grey: a grey pill is indistinguishable from "Not started"
// at a glance, which is the one distinction the rollout list has to make.
const STATUS_BADGE_CLASS: Partial<Record<ImportStatus, string>> = {
  queued: "border-blue-500/40 bg-blue-50 text-blue-700",
  partial: "border-amber-500/40 bg-amber-50 text-amber-700",
};

const STATUS_CARD_RING: Record<ImportStatus, string> = {
  not_started: "ring-1 ring-border",
  // Same blue family as in_progress, one step down in weight — queued and running
  // are the two live states, and the intensity is what separates them.
  queued: "ring-1 ring-blue-500/30",
  in_progress: "ring-2 ring-blue-500/40",
  partial: "ring-2 ring-amber-500/40",
  completed: "ring-2 ring-emerald-500/40",
  error: "ring-2 ring-destructive/60",
};

function StatusBadge({ status }: { status: ImportStatus }) {
  return (
    <Badge variant={STATUS_VARIANT[status]} className={STATUS_BADGE_CLASS[status]}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function ClassificationStatusBadge({
  classification,
}: {
  classification: ClassificationStatusEntry;
}) {
  const status = classificationRollup(classification);
  return (
    <Badge variant={STATUS_VARIANT[status]} className={STATUS_BADGE_CLASS[status]}>
      {CLASSIFICATION_STATUS_LABEL[status]}
    </Badge>
  );
}

function WatchBadge({ watch }: { watch: WatchStatusEntry }) {
  const failing = watch.last_run_status === "error" && watch.consecutive_failures > 0;
  const title = failing
    ? `Watch mode — ${watch.consecutive_failures} failed tick${watch.consecutive_failures === 1 ? "" : "s"}`
    : `Watch mode — last sync ${formatRelative(watch.last_synced_at)}`;
  return (
    <Badge
      variant="outline"
      title={title}
      className={
        failing
          ? "gap-1 border-destructive/50 bg-destructive/10 text-destructive"
          : "gap-1 border-emerald-500/40 bg-emerald-50 text-emerald-700"
      }
    >
      <Eye className="size-3" />
      Watching
    </Badge>
  );
}

function rollupStatus(s: SiteImportStatus | undefined): ImportStatus {
  if (!s) return "not_started";
  const subs = [s.site.status, s.users.status, s.waste.status];
  if (subs.includes("error")) return "error";
  if (subs.includes("in_progress")) return "in_progress";
  // POS2-1092: a queued job outranks the completed/partial verdicts so the
  // rollout's waiting sites are visible in the list without opening each card.
  if (subs.includes("queued")) return "queued";
  if (subs.every((x) => x === "completed")) return "completed";
  if (subs.some((x) => x === "completed")) return "partial";
  return "not_started";
}

// POS2-699: map classification state onto the import-status pill vocabulary.
// "In progress" = actively classifying (open-ended config, or a run still
// draining). Otherwise the pill reflects the *event* tallies so it can never
// contradict the counts shown beside it — events can be classified by manual
// Analyse or the food-map copy without the DataBridge config being enabled, so
// a config-only pill would wrongly read "Not started" next to N classified.
function classificationRollup(c: ClassificationStatusEntry): ImportStatus {
  const draining = c.counts.pending + c.counts.running > 0;
  if (c.enabled && (c.open_ended || draining)) return "in_progress";
  const sc = c.source_counts;
  const classified = sc ? sc.ai + sc.food_map : c.counts.done + c.counts.failed;
  const pending = sc ? sc.pending : c.counts.pending + c.counts.running;
  if (classified === 0) return "not_started";
  return pending > 0 ? "partial" : "completed";
}

interface UsersSnapshotResult {
  rows_read: number;
  rows_written: number;
  rows_removed: number;
}

/**
 * POS2-1383: copy the old platform's users into our own table, so Invite users keeps working on
 * the Sites page after the old database is switched off. Global, like the credentials snapshot,
 * and each run replaces the last. Taken as the last step before the decommission (POS2-1355).
 */
function UsersSnapshotCard() {
  const headers = useApiHeaders();

  const snapshot = useMutation<UsersSnapshotResult, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/databridge/users/snapshot`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      const json = (await res.json()) as { data: UsersSnapshotResult };
      return json.data;
    },
  });

  return (
    <div className="rounded border p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Legacy users</p>
          <p className="mt-1 max-w-xl text-xs text-muted-foreground">
            Snapshot every live old-platform user with their old sites, so the remaining ones can
            still be invited from the Sites page once the old database is gone. Each run replaces
            the last. Run it again as the last step before the old database is switched off.
          </p>
        </div>
        <Button variant="outline" onClick={() => snapshot.mutate()} disabled={snapshot.isPending}>
          {snapshot.isPending ? "Snapshotting…" : "Snapshot legacy users"}
        </Button>
      </div>

      {snapshot.data && (
        <p className="mt-2 text-xs text-muted-foreground">
          Read {snapshot.data.rows_read} live users, wrote {snapshot.data.rows_written}, removed{" "}
          {snapshot.data.rows_removed} the old system no longer has.
        </p>
      )}

      {snapshot.isError && (
        <p className="mt-2 font-mono text-xs text-destructive">{snapshot.error.message}</p>
      )}
    </div>
  );
}

interface CredentialsSnapshotResult {
  rows_read: number;
  rows_written: number;
  rows_skipped_non_pbkdf2: number;
}

/**
 * POS2-1309: copy the old platform's password hashes into our own table, once.
 *
 * **Global rather than per site**, which is why it lives above the site cards instead of in a
 * card's Actions menu: a hash is keyed by address, and a user reaches sites both directly and
 * through a parent group, so a per-site pass would read the same person once per site and miss
 * anybody assigned to neither.
 *
 * The deadline is the reason this is prominent. The old database is switched off at the end of
 * September 2026 and this is the only step of the carry-over that cannot be repeated afterwards.
 */
function CredentialsSnapshotCard() {
  const headers = useApiHeaders();

  const snapshot = useMutation<CredentialsSnapshotResult, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/databridge/credentials/snapshot`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      const json = (await res.json()) as { data: CredentialsSnapshotResult };
      return json.data;
    },
  });

  return (
    <div className="rounded border p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Legacy credentials</p>
          <p className="mt-1 max-w-xl text-xs text-muted-foreground">
            Snapshot every live old-platform password hash so migrated users can sign in with the
            password they already have. Idempotent, and it never un-spends a hash already used. Must
            run before the old database is switched off at the end of September 2026.
          </p>
        </div>
        <Button variant="outline" onClick={() => snapshot.mutate()} disabled={snapshot.isPending}>
          {snapshot.isPending ? "Snapshotting…" : "Snapshot legacy credentials"}
        </Button>
      </div>

      {snapshot.data && (
        <p className="mt-2 text-xs text-muted-foreground">
          Read {snapshot.data.rows_read} live users, wrote {snapshot.data.rows_written}, skipped{" "}
          {snapshot.data.rows_skipped_non_pbkdf2} that were not PBKDF2. Check the read count against
          the old system&apos;s user count while that database still answers.
        </p>
      )}

      {snapshot.isError && (
        <p className="mt-2 font-mono text-xs text-destructive">{snapshot.error.message}</p>
      )}
    </div>
  );
}

/** The site's classification endpoint, which the shared schedule row and dialog call. */
function classificationUrl(oldSiteId: number): string {
  return `${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/classification`;
}

export default function AdminDataBridgePage() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importWasteOpen, setImportWasteOpen] = useState(false);
  const [queueWasteOpen, setQueueWasteOpen] = useState(false);
  const [classificationOpen, setClassificationOpen] = useState(false);
  const [applyMapOpen, setApplyMapOpen] = useState(false);
  const [truncatedPhotosOpen, setTruncatedPhotosOpen] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);

  // Defensive: if the user manages to navigate away (e.g. via keyboard shortcut
  // or the sidebar collapse), make sure the detail Sheet and import Dialog
  // don't linger.
  useEffect(() => {
    setSelectedSiteId(null);
    setImportDialogOpen(false);
    setImportWasteOpen(false);
    setQueueWasteOpen(false);
    setClassificationOpen(false);
    setTruncatedPhotosOpen(false);
    setWatchOpen(false);
  }, [location.pathname]);

  function refreshClassification(oldSiteId: number) {
    void queryClient.invalidateQueries({
      queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
    });
    void queryClient.invalidateQueries({ queryKey: ["admin", "databridge", "all-statuses"] });
  }

  const sitesQuery = useQuery<OldSiteSummary[], Error>({
    queryKey: ["admin", "databridge", "sites"],
    queryFn: () => fetchJson<OldSiteSummary[]>(`${API_URL}/api/v1/admin/databridge/sites`, headers),
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    retry: false,
  });

  const detailQuery = useQuery<OldSiteDetail, Error>({
    queryKey: ["admin", "databridge", "site", selectedSiteId],
    queryFn: () =>
      fetchJson<OldSiteDetail>(
        `${API_URL}/api/v1/admin/databridge/sites/${selectedSiteId}`,
        headers,
      ),
    enabled: selectedSiteId !== null,
    retry: false,
  });

  const statusQuery = useQuery<SiteImportStatus, Error>({
    queryKey: ["admin", "databridge", "site", selectedSiteId, "status"],
    queryFn: () =>
      fetchJson<SiteImportStatus>(
        `${API_URL}/api/v1/admin/databridge/sites/${selectedSiteId}/status`,
        headers,
      ),
    enabled: selectedSiteId !== null,
    retry: false,
    // Background-task imports (POS2-252 waste) need polling so the badge
    // moves from in_progress → completed without a manual refresh.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      // POS2-1092: "queued" polls too — the worker promotes the row on its own,
      // so the card has to move to in_progress without a manual refresh.
      const inProgress = [data.site, data.users, data.waste].some(
        (e) => e.status === "in_progress" || e.status === "queued",
      );
      // POS2-648: also poll while a classification run is draining.
      const classifying =
        data.classification.counts.pending + data.classification.counts.running > 0;
      // POS2-714: poll while a Copy Old Classifications run is in flight so the
      // button re-enables when it completes.
      const copying = data.classification_copy?.status === "in_progress";
      return inProgress || classifying || copying ? SITE_POLL_INTERVAL_MS : false;
    },
  });

  // POS2-717: the AI-classified/auto-mapped/pending counts are COUNT(*) over
  // waste_events — split out of the polled /status so the card body renders
  // immediately and only these tiles show a loading state.
  const classificationCountsQuery = useQuery<ClassificationSourceCounts | null, Error>({
    queryKey: ["admin", "databridge", "site", selectedSiteId, "classification-counts"],
    queryFn: () =>
      fetchJson<ClassificationSourceCounts | null>(
        `${API_URL}/api/v1/admin/databridge/sites/${selectedSiteId}/classification-counts`,
        headers,
      ),
    enabled: selectedSiteId !== null && statusQuery.data?.waste.status === "completed",
    retry: false,
    refetchInterval: (query) =>
      query.state.data && query.state.data.pending > 0 ? SITE_POLL_INTERVAL_MS : false,
  });

  // POS2-623: single batched endpoint replaces the per-site Promise.all fanout.
  // Sites with no databridge_imports or watch row are absent from the response;
  // rollupStatus(undefined) → "not_started" so the rendering path is unchanged.
  const allStatusesQuery = useQuery<Record<string, SiteImportStatus>, Error>({
    queryKey: ["admin", "databridge", "all-statuses"],
    queryFn: () =>
      fetchJson<Record<string, SiteImportStatus>>(
        `${API_URL}/api/v1/admin/databridge/sites/statuses`,
        headers,
      ),
    enabled: !!sitesQuery.data && sitesQuery.data.length > 0,
    retry: false,
  });

  // POS2-1102: the states in which this site already has a waste job in the queue —
  // queueing again would be a server-side no-op, so both import buttons are disabled.
  const wasteStatus = statusQuery.data?.waste.status;
  const wasteJobPending = wasteStatus === "queued" || wasteStatus === "in_progress";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-medium">DataBridge</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Import sites, users, and waste history from the old FoodSight system.
        </p>
      </div>

      <CredentialsSnapshotCard />
      <UsersSnapshotCard />

      {sitesQuery.isLoading && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      )}

      {sitesQuery.isError && sitesQuery.error instanceof ConnectionError && (
        <div className="flex flex-col items-center gap-3 rounded border border-dashed py-12 text-center">
          <Database className="size-8 text-muted-foreground" />
          <p className="text-sm font-medium">Old system not connected</p>
          <p className="max-w-sm text-xs text-muted-foreground">{sitesQuery.error.message}</p>
        </div>
      )}

      {sitesQuery.isError && !(sitesQuery.error instanceof ConnectionError) && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Failed to load sites</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">{sitesQuery.error.message}</p>
        </div>
      )}

      {sitesQuery.data && sitesQuery.data.length === 0 && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          No sites found in the old system.
        </div>
      )}

      {sitesQuery.data && sitesQuery.data.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {sitesQuery.data.map((site) => {
            const siteStatus = allStatusesQuery.data?.[site.site_id];
            const status = rollupStatus(siteStatus);
            const watch = siteStatus?.watch;
            const classification = siteStatus?.classification;
            return (
              <button
                key={site.site_id}
                type="button"
                onClick={() => setSelectedSiteId(site.site_id)}
                className={`rounded-lg bg-card p-4 text-left transition hover:bg-accent ${STATUS_CARD_RING[status]}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{site.name}</p>
                    <p className="text-xs text-muted-foreground">ID {site.site_id}</p>
                  </div>
                  {/* Wrap the badges (POS2-999): three tags overflow a narrow grid card and
                      clip "Partially imported"; let them wrap and the name truncate instead. */}
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {classification?.enabled && (
                      <ClassificationBadge classification={classification} />
                    )}
                    {watch?.enabled && <WatchBadge watch={watch} />}
                    <StatusBadge status={status} />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <Stat label="Devices" value={site.device_count} />
                  <Stat label="Events" value={site.event_count} />
                  <Stat label="Users" value={site.user_count} />
                </div>
                {site.last_event_date && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Last event {formatDate(site.last_event_date)}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}

      <ImportSiteDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        oldSiteId={selectedSiteId}
        oldSiteName={detailQuery.data?.name ?? null}
      />

      <ImportWasteDialog
        open={importWasteOpen}
        onOpenChange={setImportWasteOpen}
        oldSiteId={selectedSiteId}
        oldSiteName={detailQuery.data?.name ?? null}
      />

      <QueueWasteImportDialog
        open={queueWasteOpen}
        onOpenChange={setQueueWasteOpen}
        oldSiteId={selectedSiteId}
        oldSiteName={detailQuery.data?.name ?? null}
      />

      {selectedSiteId !== null && (
        <EnableClassificationDialog
          open={classificationOpen}
          onOpenChange={setClassificationOpen}
          classificationUrl={classificationUrl(selectedSiteId)}
          siteName={detailQuery.data?.name ?? null}
          devices={detailQuery.data?.devices ?? []}
          watchCopyingOldLabels={
            !!statusQuery.data &&
            statusQuery.data.watch.enabled &&
            statusQuery.data.watch.apply_food_map
          }
          onChanged={() => refreshClassification(selectedSiteId)}
        />
      )}

      <ApplyFoodMapDialog
        open={applyMapOpen}
        onOpenChange={setApplyMapOpen}
        oldSiteId={selectedSiteId}
        oldSiteName={detailQuery.data?.name ?? null}
      />

      <TruncatedPhotosDialog
        open={truncatedPhotosOpen}
        onOpenChange={setTruncatedPhotosOpen}
        oldSiteId={selectedSiteId}
        oldSiteName={detailQuery.data?.name ?? null}
        devices={detailQuery.data?.devices ?? []}
      />

      <EnableWatchModeDialog
        open={watchOpen}
        onOpenChange={setWatchOpen}
        oldSiteId={selectedSiteId}
        oldSiteName={detailQuery.data?.name ?? null}
        classificationOpenEnded={
          !!statusQuery.data &&
          statusQuery.data.classification.enabled &&
          statusQuery.data.classification.open_ended
        }
      />

      <Sheet
        open={
          selectedSiteId !== null &&
          !importDialogOpen &&
          !importWasteOpen &&
          !queueWasteOpen &&
          !classificationOpen &&
          !applyMapOpen &&
          !truncatedPhotosOpen &&
          !watchOpen
        }
        onOpenChange={(v) => !v && setSelectedSiteId(null)}
        modal={false}
      >
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{detailQuery.data?.name ?? "Site detail"}</SheetTitle>
          </SheetHeader>

          {detailQuery.isLoading && (
            <div className="space-y-2 px-4 pb-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}

          {detailQuery.isError && (
            <div className="px-4 pb-4 text-sm text-destructive">{detailQuery.error.message}</div>
          )}

          {detailQuery.data && (
            <div className="space-y-5 px-4 pb-6 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Devices" value={detailQuery.data.device_count} />
                <Stat label="Events" value={detailQuery.data.event_count} />
                <Stat label="Users" value={detailQuery.data.user_count} />
              </div>

              {statusQuery.data && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Import status</p>
                  <div className="space-y-1">
                    <StatusRow label="Site & Devices" entry={statusQuery.data.site} />
                    <StatusRow label="Users" entry={statusQuery.data.users} />
                    <StatusRow label="Waste" entry={statusQuery.data.waste} />
                  </div>
                </div>
              )}

              {statusQuery.data && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Classification status</p>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between rounded bg-muted/30 px-2 py-1.5">
                      <span className="text-xs">AI classification</span>
                      <ClassificationStatusBadge classification={statusQuery.data.classification} />
                    </div>
                  </div>
                  {/* POS2-717: counts load independently — only these tiles
                      show a skeleton; the rest of the card is already rendered. */}
                  {statusQuery.data.waste.status === "completed" &&
                    (classificationCountsQuery.data ? (
                      <div className="grid grid-cols-3 gap-2">
                        <Stat label="AI-classified" value={classificationCountsQuery.data.ai} />
                        <Stat label="Auto-mapped" value={classificationCountsQuery.data.food_map} />
                        <Stat label="Pending" value={classificationCountsQuery.data.pending} />
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {[0, 1, 2].map((i) => (
                          <Skeleton key={i} className="h-12 w-full" />
                        ))}
                      </div>
                    ))}
                </div>
              )}

              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Actions</p>
                <TooltipProvider>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setImportDialogOpen(true)}
                      disabled={statusQuery.data?.site.status === "in_progress"}
                    >
                      {statusQuery.data?.site.status === "completed"
                        ? "Re-run Import Site & Devices"
                        : "Import Site & Devices"}
                    </Button>
                    {statusQuery.data?.site.status === "completed" ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setImportWasteOpen(true)}
                        disabled={wasteJobPending}
                      >
                        {statusQuery.data?.waste.status === "completed"
                          ? "Re-run Import Waste Data"
                          : statusQuery.data?.waste.status === "in_progress"
                            ? "Import in progress…"
                            : statusQuery.data?.waste.status === "queued"
                              ? "Queued for import…"
                              : "Import Waste Data"}
                      </Button>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="w-full">
                            <Button variant="outline" className="w-full" disabled>
                              Import Waste Data
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>Import the site first</TooltipContent>
                      </Tooltip>
                    )}
                    {/* POS2-1102: same import, deferred — sits under the immediate one so
                        "now" vs "when the box is free" reads as a choice, not lore. */}
                    {statusQuery.data?.site.status === "completed" &&
                      (wasteJobPending ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="w-full">
                              <Button variant="outline" className="w-full" disabled>
                                Queue Waste Import
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {statusQuery.data?.waste.status === "queued"
                              ? "Already queued — the worker will start it in turn"
                              : "This site's import is already running"}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setQueueWasteOpen(true)}
                        >
                          Queue Waste Import
                        </Button>
                      ))}
                    {/* POS2-1306: heal photos copied mid-upload before the
                        completeness check landed. Sits with the waste import
                        because that is what produced them, and above
                        classification because a repaired photo is what the
                        classifier should be looking at. */}
                    {statusQuery.data?.waste.status === "completed" && (
                      <div className="space-y-1">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setTruncatedPhotosOpen(true)}
                        >
                          Find truncated photos
                        </Button>
                        <p className="text-[10px] text-muted-foreground">
                          Photos copied while a device was still uploading — re-copy from Azure and
                          re-classify
                        </p>
                      </div>
                    )}
                    {/* Classify imported events: from old labels (fast) then AI for the rest */}
                    {statusQuery.data?.waste.status === "completed" && (
                      <div className="space-y-1">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setApplyMapOpen(true)}
                          disabled={statusQuery.data.classification_copy?.status === "in_progress"}
                        >
                          {statusQuery.data.classification_copy?.status === "in_progress"
                            ? "Copying old classifications…"
                            : "Copy Old Classifications"}
                        </Button>
                        <p className="text-[10px] text-muted-foreground">
                          Classify imported events from old-system labels (skip the AI)
                        </p>
                      </div>
                    )}
                    {selectedSiteId !== null && statusQuery.data && (
                      <ClassificationToggleRow
                        classificationUrl={classificationUrl(selectedSiteId)}
                        classification={statusQuery.data.classification}
                        enableBlockedReason={
                          statusQuery.data.waste.status === "completed"
                            ? null
                            : "Import waste data first"
                        }
                        onConfigure={() => setClassificationOpen(true)}
                        onChanged={() => refreshClassification(selectedSiteId)}
                      />
                    )}
                    {/* Watch: keep syncing new old-system events going forward */}
                    {selectedSiteId !== null && statusQuery.data && (
                      <WatchToggleRow
                        oldSiteId={selectedSiteId}
                        watch={statusQuery.data.watch}
                        wasteImported={statusQuery.data.waste.status === "completed"}
                        onConfigure={() => setWatchOpen(true)}
                      />
                    )}
                  </div>
                </TooltipProvider>
              </div>

              {detailQuery.data.devices.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    Devices (first {detailQuery.data.devices.length})
                  </p>
                  <ul className="space-y-1 text-xs">
                    {detailQuery.data.devices.map((d) => (
                      <li key={d.serial} className="flex justify-between">
                        <span className="font-mono">{d.serial}</span>
                        <span className="text-muted-foreground">{d.name ?? "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {detailQuery.data.users.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    Users (first {detailQuery.data.users.length})
                  </p>
                  <ul className="space-y-1 text-xs">
                    {detailQuery.data.users.map((u) => (
                      <li key={u.user_id} className="flex justify-between">
                        <span>{u.email}</span>
                        <span className="text-muted-foreground">{u.full_name ?? "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {detailQuery.data.last_events.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Recent events</p>
                  <ul className="space-y-1 text-xs">
                    {detailQuery.data.last_events.map((e) => (
                      <li key={e.event_id} className="flex justify-between">
                        <span>{formatDate(e.logged_at)}</span>
                        <span className="text-muted-foreground">
                          {e.item_weight !== null ? `${e.item_weight} kg` : "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// POS2-699: a quiet info icon that carries reference detail in a tooltip,
// keeping the control groups minimal instead of always-on helper paragraphs.
function InfoHint({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="More information"
            className="text-muted-foreground/70 hover:text-foreground"
          >
            <Info className="size-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[18rem] text-[11px] leading-snug">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded bg-muted/40 px-2 py-1.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value.toLocaleString()}</p>
    </div>
  );
}

function StatusRow({ label, entry }: { label: string; entry: ImportStatusEntry }) {
  return (
    <div className="flex items-center justify-between rounded bg-muted/30 px-2 py-1.5">
      <span className="text-xs">{label}</span>
      <StatusBadge status={entry.status} />
    </div>
  );
}

function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
  return `${Math.floor(secs / 86400)} d ago`;
}

function WatchToggleRow({
  oldSiteId,
  watch,
  wasteImported,
  onConfigure,
}: {
  oldSiteId: number;
  watch: WatchStatusEntry;
  wasteImported: boolean;
  onConfigure: () => void;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // POS2-699: enabling watch mode (with its old-system-classification options)
  // happens in EnableWatchModeDialog — consistent with the other actions, which
  // all open a modal. Disabling stays a one-click inline action (mirrors
  // ClassificationToggleRow).
  const disable = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/watch`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: false }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? `${res.status}: ${res.statusText}`);
      }
    },
    onMutate: () => setSubmitError(null),
    onError: (err: Error) => setSubmitError(err.message),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "all-statuses"],
      });
    },
  });

  // POS2-699: with apply_food_map off, a watch tick only imports events (as
  // `processing`) — it doesn't classify anything itself. Don't claim
  // "AI-classifying"; the AI only runs if classification is separately enabled.
  const subLabel = watch.enabled
    ? watch.last_run_status === "error"
      ? `${watch.consecutive_failures} failed tick${watch.consecutive_failures === 1 ? "" : "s"} — ${watch.last_error ?? "see logs"}`
      : `Last sync: ${formatRelative(watch.last_synced_at)} · ${watch.apply_food_map ? "copying old labels" : "not copying old labels"}`
    : "Auto-sync new events from the old system";

  return (
    <div className="space-y-1">
      {wasteImported ? (
        <Button
          variant={watch.enabled ? "outline" : "default"}
          className="w-full"
          onClick={() => (watch.enabled ? disable.mutate() : onConfigure())}
          disabled={disable.isPending}
        >
          {disable.isPending
            ? "Saving…"
            : watch.enabled
              ? "Disable Watch Mode"
              : "Enable Watch Mode"}
        </Button>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="w-full">
              <Button variant="default" className="w-full" disabled>
                Enable Watch Mode
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Import waste data first</TooltipContent>
        </Tooltip>
      )}
      <p
        className={`text-[10px] ${watch.last_run_status === "error" && watch.enabled ? "text-destructive" : "text-muted-foreground"}`}
      >
        {subLabel}
      </p>
      {submitError && <p className="text-[10px] text-destructive">{submitError}</p>}
    </div>
  );
}

function EnableWatchModeDialog({
  open,
  onOpenChange,
  oldSiteId,
  oldSiteName,
  classificationOpenEnded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  oldSiteId: number | null;
  oldSiteName: string | null;
  classificationOpenEnded: boolean;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [applyFoodMap, setApplyFoodMap] = useState(true);
  const [confirmLowerTiers, setConfirmLowerTiers] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // POS2-699: the first focusable element is the info-icon — letting the dialog
  // auto-focus it pops its tooltip on open. Direct initial focus to the primary
  // button instead.
  const confirmRef = useRef<HTMLButtonElement>(null);

  // POS2-699: copy old labels would race an open-ended AI classification config
  // (which auto-classifies every synced event), so force copy off in that case.
  const copyOn = applyFoodMap && !classificationOpenEnded;

  const enableMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/watch`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          enabled: true,
          apply_food_map: copyOn,
          confirm_lower_tier_maps: copyOn && confirmLowerTiers,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? `${res.status}: ${res.statusText}`);
      }
    },
    onMutate: () => setSubmitError(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "databridge", "all-statuses"] });
      onOpenChange(false);
    },
    onError: (err: Error) => setSubmitError(err.message),
  });

  useEffect(() => {
    if (!open) {
      setApplyFoodMap(true);
      setConfirmLowerTiers(false);
      setSubmitError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          confirmRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Enable Watch Mode</DialogTitle>
          <DialogDescription>
            {oldSiteName ?? "Select a site"} → keep syncing new old-system events going forward, and
            choose how each sync handles the old-system classifications.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <p className="text-xs font-medium text-muted-foreground">
                Old-system classifications
              </p>
              <InfoHint text="On = copy old labels and skip the AI. Confirmed as truth only for high-confidence specific-item (L4) matches; category-level and medium/low matches appear as unconfirmed suggestions a chef can correct." />
            </div>
            <label
              htmlFor="watch-apply-map"
              className={`flex items-start gap-2 text-xs ${classificationOpenEnded ? "opacity-50" : ""}`}
            >
              <Checkbox
                id="watch-apply-map"
                checked={copyOn}
                disabled={classificationOpenEnded}
                onCheckedChange={(checked) => setApplyFoodMap(checked === true)}
                className="mt-0.5"
              />
              <span>Copy old-system classifications on each sync (mapped foods skip the AI)</span>
            </label>
            <label
              htmlFor="watch-confirm-lower"
              className={`flex items-start gap-2 pl-6 text-xs ${copyOn ? "" : "opacity-50"}`}
            >
              <Checkbox
                id="watch-confirm-lower"
                checked={copyOn && confirmLowerTiers}
                disabled={!copyOn}
                onCheckedChange={(checked) => setConfirmLowerTiers(checked === true)}
                className="mt-0.5"
              />
              <span>Also confirm category-level (L1–L3) matches</span>
            </label>
            {classificationOpenEnded && (
              <p className="text-xs text-muted-foreground">
                AI classification is running open-ended for this site — it already classifies every
                synced event, so copying old labels would conflict with it and is disabled. Disable
                classification first if you want to copy old labels instead.
              </p>
            )}
          </div>

          {submitError && <p className="text-xs font-mono text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            onClick={() => enableMutation.mutate()}
            disabled={enableMutation.isPending}
          >
            {enableMutation.isPending ? "Enabling…" : "Enable Watch Mode"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportSiteDialog({
  open,
  onOpenChange,
  oldSiteId,
  oldSiteName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  oldSiteId: number | null;
  oldSiteName: string | null;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const accountsQuery = useQuery<Account[], Error>({
    queryKey: ["accounts"],
    queryFn: () => fetchJson<Account[]>(`${API_URL}/api/v1/accounts`, headers),
    enabled: open && !!headers.Authorization.replace("Bearer ", ""),
  });

  // Auto-pick the only account if the org has just one.
  useEffect(() => {
    if (!open) return;
    const accounts = accountsQuery.data ?? [];
    if (accountId === null && accounts.length === 1) {
      setAccountId(accounts[0].id);
    }
  }, [open, accountsQuery.data, accountId]);

  const previewQuery = useQuery<SiteImportPreview, Error>({
    queryKey: ["admin", "databridge", "preview", oldSiteId, accountId],
    queryFn: () =>
      fetchJson<SiteImportPreview>(
        `${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/import/site/preview?account_id=${accountId}`,
        headers,
      ),
    enabled: open && oldSiteId !== null && accountId !== null,
    retry: false,
  });

  const importMutation = useMutation<SiteImportResult, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/import/site`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: accountId }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      const json = (await res.json()) as { data: SiteImportResult };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "databridge", "all-statuses"] });
      onOpenChange(false);
    },
    onError: (err) => setSubmitError(err.message),
  });

  // Reset transient state when dialog closes. `importMutation` is intentionally
  // excluded from deps — TanStack Query's mutation result reshapes on every
  // render, and including it triggers an infinite render loop via reset()
  // notifying subscribers, which produces a new mutation result, etc.
  // The reset function reference is stable across renders.
  const resetImport = importMutation.reset;
  useEffect(() => {
    if (!open) {
      setSubmitError(null);
      resetImport();
    }
  }, [open, resetImport]);

  const preview = previewQuery.data;
  const submitDisabled =
    !preview || preview.already_imported || importMutation.isPending || accountId === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Site &amp; Devices</DialogTitle>
          <DialogDescription>
            {oldSiteName ?? "Select a site"} → new tenant under the chosen account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          <div className="space-y-1.5">
            <p className="text-xs font-medium">Account</p>
            {accountsQuery.isLoading && <Skeleton className="h-9 w-full" />}
            {accountsQuery.isError && (
              <p className="text-xs text-destructive">
                Could not load accounts: {accountsQuery.error.message}
              </p>
            )}
            {accountsQuery.data && accountsQuery.data.length === 0 && (
              <p className="text-xs text-destructive">
                No accounts in this org. Create one in Settings → Accounts first.
              </p>
            )}
            {accountsQuery.data && accountsQuery.data.length > 0 && (
              <Select value={accountId ?? undefined} onValueChange={setAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick an account…" />
                </SelectTrigger>
                <SelectContent>
                  {accountsQuery.data.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {accountId !== null && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium">Will create</p>
              {previewQuery.isLoading && <Skeleton className="h-20 w-full" />}
              {previewQuery.isError && (
                <p className="text-xs text-destructive">{previewQuery.error.message}</p>
              )}
              {preview && (
                <div className="rounded border bg-muted/30 p-3 text-xs space-y-1">
                  <p>
                    Site <span className="font-medium">{preview.site_name}</span>
                  </p>
                  <p>
                    Default kitchen <span className="font-medium">{preview.kitchen_name}</span>
                  </p>
                  <p>
                    {preview.device_count} device{preview.device_count === 1 ? "" : "s"}
                    {preview.device_collisions.length > 0 && (
                      <span className="text-amber-600">
                        {" "}
                        — {preview.device_collisions.length} already in new system, will be skipped
                      </span>
                    )}
                  </p>
                  {preview.already_imported && (
                    <p className="text-amber-700">
                      Already imported as site {preview.existing_new_site_id?.slice(0, 8)}…
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {submitError && <p className="text-xs font-mono text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => importMutation.mutate()} disabled={submitDisabled}>
            {importMutation.isPending ? "Importing…" : "Confirm import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportWasteDialog({
  open,
  onOpenChange,
  oldSiteId,
  oldSiteName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  oldSiteId: number | null;
  oldSiteName: string | null;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [applyMap, setApplyMap] = useState(true);

  const previewQuery = useQuery<WasteImportPreview, Error>({
    queryKey: ["admin", "databridge", "waste-preview", oldSiteId],
    queryFn: () =>
      fetchJson<WasteImportPreview>(
        `${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/import/waste/preview`,
        headers,
      ),
    enabled: open && oldSiteId !== null,
    retry: false,
  });

  const triggerMutation = useMutation<WasteImportTrigger, Error, void>({
    mutationFn: async () => {
      const res = await fetch(
        `${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/import/waste`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ apply_map: applyMap }),
        },
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      const json = (await res.json()) as { data: WasteImportTrigger };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "databridge", "all-statuses"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "waste-preview", oldSiteId],
      });
    },
    onError: (err) => setSubmitError(err.message),
  });

  const resetMutation = triggerMutation.reset;
  useEffect(() => {
    if (!open) {
      setSubmitError(null);
      setApplyMap(true);
      resetMutation();
    }
  }, [open, resetMutation]);

  const preview = previewQuery.data;
  const trigger = triggerMutation.data;
  const remainingToImport = preview
    ? Math.max(preview.total_events - preview.already_imported, 0)
    : 0;
  const submitDisabled = !preview || triggerMutation.isPending || remainingToImport === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Waste Data</DialogTitle>
          <DialogDescription>
            {oldSiteName ?? "Select a site"} → migrate events and transfer images from the old
            system. Runs in the background; watch the status badge for progress.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          {previewQuery.isLoading && <Skeleton className="h-32 w-full" />}
          {previewQuery.isError && (
            <p className="text-xs text-destructive">{previewQuery.error.message}</p>
          )}
          {preview && !trigger && (
            <div className="rounded border bg-muted/30 p-3 text-xs space-y-2">
              <div className="flex justify-between">
                <span>Total events for site</span>
                <span className="font-medium">{preview.total_events}</span>
              </div>
              <div className="flex justify-between">
                <span>Events with image</span>
                <span className="font-medium">{preview.events_with_image}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Estimated image transfer</span>
                <span>~{preview.estimated_image_size_mb} MB</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Already imported</span>
                <span>{preview.already_imported}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">To import this run</span>
                <span className="font-medium">{remainingToImport}</span>
              </div>
              {preview.devices_not_in_new_system.length > 0 && (
                <div className="border-t pt-2 text-amber-700">
                  <p className="font-medium">
                    {preview.devices_not_in_new_system.length} device
                    {preview.devices_not_in_new_system.length === 1 ? "" : "s"} not in new system —
                    events from {preview.devices_not_in_new_system.length === 1 ? "it" : "them"}{" "}
                    will be skipped:
                  </p>
                  <ul className="mt-1 space-y-0.5 font-mono">
                    {preview.devices_not_in_new_system.slice(0, 5).map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                    {preview.devices_not_in_new_system.length > 5 && (
                      <li className="text-muted-foreground">
                        … +{preview.devices_not_in_new_system.length - 5} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {preview && !trigger && (
            <label htmlFor="waste-apply-map" className="flex items-start gap-2 text-xs">
              <Checkbox
                id="waste-apply-map"
                checked={applyMap}
                onCheckedChange={(checked) => setApplyMap(checked === true)}
                className="mt-0.5"
              />
              <span>
                Auto-classify mapped foods (skip AI)
                <span className="block text-muted-foreground">
                  Confidently-mapped old foods are classified directly and marked confirmed; the
                  rest run through the AI. Uncheck to import everything for AI classification.
                </span>
              </span>
            </label>
          )}

          {trigger && (
            <div className="rounded border bg-emerald-50 p-3 text-xs text-emerald-900 space-y-1">
              <p className="font-medium">
                {trigger.status === "completed" ? "Already complete" : "Import scheduled"}
              </p>
              <p>
                {trigger.status === "completed"
                  ? "A previous run has already imported this site's waste data. Nothing to do."
                  : "Running in the background. The status badge will update as batches complete; you can close this dialog."}
              </p>
              <p className="font-mono text-emerald-700">
                import_id {trigger.import_id.slice(0, 8)}…
              </p>
            </div>
          )}

          {submitError && <p className="text-xs font-mono text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {trigger ? "Close" : "Cancel"}
          </Button>
          {!trigger && (
            <Button onClick={() => triggerMutation.mutate()} disabled={submitDisabled}>
              {triggerMutation.isPending ? "Scheduling…" : "Confirm import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// POS2-1102: queue this site's waste import instead of running it now. Sibling to
// ImportWasteDialog — same preview (shared query key, so opening this costs no extra
// request) and the same apply_map choice, differing only in when the work runs.
//
// Kept as its own dialog rather than a mode on ImportWasteDialog: they'd share two
// widgets but fork on title, confirm copy, outcome panel and disabled logic, and two
// flat dialogs are fewer states than one branching one.
function QueueWasteImportDialog({
  open,
  onOpenChange,
  oldSiteId,
  oldSiteName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  oldSiteId: number | null;
  oldSiteName: string | null;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [applyMap, setApplyMap] = useState(true);

  const previewQuery = useQuery<WasteImportPreview, Error>({
    queryKey: ["admin", "databridge", "waste-preview", oldSiteId],
    queryFn: () =>
      fetchJson<WasteImportPreview>(
        `${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/import/waste/preview`,
        headers,
      ),
    enabled: open && oldSiteId !== null,
    retry: false,
  });

  const enqueueMutation = useMutation<EnqueueResult, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/databridge/imports/waste/enqueue`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          old_site_ids: [oldSiteId],
          apply_map: applyMap,
          confirm_lower_tiers: false,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      const json = (await res.json()) as { data: EnqueueResult };
      return json.data;
    },
    // Clear on every attempt, not on success: a retry that succeeds would otherwise
    // render the green panel with the previous failure's red line still under it.
    onMutate: () => setSubmitError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "databridge", "all-statuses"] });
    },
    onError: (err) => setSubmitError(err.message),
  });

  const resetMutation = enqueueMutation.reset;
  useEffect(() => {
    if (!open) {
      setSubmitError(null);
      setApplyMap(true);
      resetMutation();
    }
  }, [open, resetMutation]);

  const preview = previewQuery.data;
  const result = enqueueMutation.data;
  const remainingToImport = preview
    ? Math.max(preview.total_events - preview.already_imported, 0)
    : 0;
  // One site in, so exactly one of the three lists carries it.
  const notQueued = result ? (result.skipped[0] ?? result.rejected[0] ?? null) : null;
  const wasQueued = !!result && result.queued.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Queue Waste Import</DialogTitle>
          <DialogDescription>
            {oldSiteName ?? "Select a site"} → run this import when the queue reaches it. One import
            runs at a time across all sites, so nothing competes for the box.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          {previewQuery.isLoading && <Skeleton className="h-32 w-full" />}
          {previewQuery.isError && (
            <p className="text-xs text-destructive">{previewQuery.error.message}</p>
          )}
          {preview && !result && (
            <div className="rounded border bg-muted/30 p-3 text-xs space-y-2">
              <div className="flex justify-between">
                <span>Total events for site</span>
                <span className="font-medium">{preview.total_events}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Estimated image transfer</span>
                <span>~{preview.estimated_image_size_mb} MB</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Already imported</span>
                <span>{preview.already_imported}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">To import when it runs</span>
                <span className="font-medium">{remainingToImport}</span>
              </div>
              {preview.devices_not_in_new_system.length > 0 && (
                <div className="border-t pt-2 text-amber-700">
                  <p className="font-medium">
                    {preview.devices_not_in_new_system.length} device
                    {preview.devices_not_in_new_system.length === 1 ? "" : "s"} not in new system —
                    events from {preview.devices_not_in_new_system.length === 1 ? "it" : "them"}{" "}
                    will be skipped:
                  </p>
                  <ul className="mt-1 space-y-0.5 font-mono">
                    {preview.devices_not_in_new_system.slice(0, 5).map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                    {preview.devices_not_in_new_system.length > 5 && (
                      <li className="text-muted-foreground">
                        … +{preview.devices_not_in_new_system.length - 5} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {preview && !result && (
            <label htmlFor="queue-apply-map" className="flex items-start gap-2 text-xs">
              <Checkbox
                id="queue-apply-map"
                checked={applyMap}
                onCheckedChange={(checked) => setApplyMap(checked === true)}
                className="mt-0.5"
              />
              <span>
                Auto-classify mapped foods (skip AI)
                <span className="block text-muted-foreground">
                  Confidently-mapped old foods are classified directly and marked confirmed; the
                  rest run through the AI. Uncheck on the largest sites so the import finishes first
                  — their map pass is long enough to be worth running on its own.
                </span>
              </span>
            </label>
          )}

          {wasQueued && (
            <div className="rounded border bg-emerald-50 p-3 text-xs text-emerald-900 space-y-1">
              <p className="font-medium">Queued</p>
              <p>
                The worker starts this import once the one ahead of it finishes. The status badge
                moves from Queued to In progress on its own; you can close this dialog.
              </p>
            </div>
          )}

          {notQueued && (
            <div className="rounded border bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
              <p className="font-medium">
                {result && result.skipped.length > 0
                  ? "Already in the queue"
                  : "Not ready to queue"}
              </p>
              <p>{notQueued.reason}</p>
            </div>
          )}

          {submitError && <p className="text-xs font-mono text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button
              onClick={() => enqueueMutation.mutate()}
              disabled={!preview || enqueueMutation.isPending}
            >
              {enqueueMutation.isPending ? "Queueing…" : "Add to queue"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * POS2-1306: what a census or a repair of truncated photos came back with.
 *
 * Field-for-field `RepairTruncatedImagesResult` in `app/schemas/databridge.py`;
 * a backend test asserts the two agree, because a hand-written interface and a
 * hand-written test mock are self-consistent and can both be wrong together.
 *
 * `photos_*` counts R2 objects and `events_*` counts waste events: the importer
 * gives two events one key when their Azure source URL matched, so an object
 * can back several events. Counts are exact; the `*_event_ids` lists are a
 * capped sample.
 */
interface TruncatedImagesResult {
  photos_scanned: number;
  photos_truncated: number;
  photos_ready: number;
  photos_still_uploading: number;
  photos_source_also_truncated: number;
  photos_missing: number;
  photos_recopied: number;
  photos_failed: number;
  events_affected: number;
  events_would_reclassify: number;
  events_skipped_confirmed: number;
  events_skipped_discarded: number;
  events_skipped_out_of_scope: number;
  events_reclassified: number;
  append_blob_devices: string[];
  ready_event_ids: string[];
  still_uploading_event_ids: string[];
  would_reclassify_event_ids: string[];
  skipped_confirmed_event_ids: string[];
  skipped_discarded_event_ids: string[];
}

/**
 * POS2-1306: find photos DataBridge copied while a device was still uploading
 * them, then heal them.
 *
 * Two steps on purpose, and the dialog is the gate between them. The census is
 * cheap and writes nothing, and it runs on open and again whenever the device
 * scope changes; the repair overwrites objects in R2 and spends Claude budget
 * re-classifying, so it waits for the button in the footer with the numbers
 * already on screen. Re-runnable until Azure retires; the last census before
 * that should read zero.
 */
function TruncatedPhotosDialog({
  open,
  onOpenChange,
  oldSiteId,
  oldSiteName,
  devices,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  oldSiteId: number | null;
  oldSiteName: string | null;
  devices: OldDeviceSummary[];
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<"all" | "specific">("all");
  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [result, setResult] = useState<TruncatedImagesResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const deviceSerials = scope === "specific" ? selectedSerials : null;
  const scopeReady = scope === "all" || selectedSerials.length > 0;

  const post = async (dryRun: boolean): Promise<TruncatedImagesResult> => {
    const res = await fetch(
      `${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/import/waste/repair-truncated-images`,
      {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ dry_run: dryRun, device_serials: deviceSerials }),
      },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { detail?: string } | null;
      throw new Error(body?.detail ?? `${res.status}: ${res.statusText}`);
    }
    return ((await res.json()) as { data: TruncatedImagesResult }).data;
  };

  const census = useQuery<TruncatedImagesResult, Error>({
    queryKey: ["admin", "databridge", "truncated-photos-census", oldSiteId, scope, selectedSerials],
    queryFn: () => post(true),
    enabled: open && oldSiteId !== null && scopeReady && result === null,
    retry: false,
  });

  const repairMutation = useMutation<TruncatedImagesResult, Error, void>({
    mutationFn: () => post(false),
    onSuccess: (data) => {
      setResult(data);
      // The repaired events go back through classification, which the site
      // card's counts render.
      void queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "classification-counts"],
      });
    },
    onError: (err) => setSubmitError(err.message),
  });

  useEffect(() => {
    if (!open) {
      setScope("all");
      setSelectedSerials([]);
      setResult(null);
      setSubmitError(null);
    }
  }, [open]);

  // A named device list replaces the automatic narrowing, so the returned
  // device names only mean "found to upload in chunks" when we asked for all.
  const probed = scope === "all";
  const ready = census.data?.photos_ready ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Find Truncated Photos</DialogTitle>
          <DialogDescription>
            {oldSiteName ?? "Select a site"} → one old-system device uploads its photo to Azure in
            chunks, so imports before the completeness check could copy a half-written JPEG and
            classify that. Finding them reads each stored object&apos;s last two bytes and writes
            nothing.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4 py-2 text-sm">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Device scope</p>
              <Select value={scope} onValueChange={(v) => setScope(v as "all" | "specific")}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All site devices</SelectItem>
                  <SelectItem value="specific">Specific devices</SelectItem>
                </SelectContent>
              </Select>
              {scope === "specific" && (
                <div className="max-h-32 space-y-1 overflow-y-auto rounded border p-2">
                  {devices.map((d) => (
                    <label key={d.serial} className="flex items-center gap-2 text-xs">
                      <Checkbox
                        checked={selectedSerials.includes(d.serial)}
                        onCheckedChange={(checked) =>
                          setSelectedSerials((prev) =>
                            checked ? [...prev, d.serial] : prev.filter((s) => s !== d.serial),
                          )
                        }
                      />
                      <span className="font-mono">{d.serial}</span>
                      <span className="text-muted-foreground">{d.name ?? ""}</span>
                    </label>
                  ))}
                  {devices.length === 0 && (
                    <p className="text-xs text-muted-foreground">No devices for this site.</p>
                  )}
                </div>
              )}
              {!scopeReady && (
                <p className="text-xs text-muted-foreground">Pick at least one device.</p>
              )}
            </div>

            {census.isFetching && <Skeleton className="h-16 w-full" />}
            {!census.isFetching && census.isError && (
              <p className="text-xs font-mono text-destructive">{census.error.message}</p>
            )}
            {!census.isFetching && census.data && (
              <div role="status" className="space-y-1 rounded border bg-muted/30 p-3 text-xs">
                <CensusSummary census={census.data} probed={probed} />
              </div>
            )}
          </div>
        )}

        {result && (
          <div
            role="status"
            className="my-2 space-y-1 rounded border bg-emerald-50 p-3 text-xs text-emerald-900"
          >
            <div>
              Repaired <span className="font-medium">{result.photos_recopied}</span> photo
              {result.photos_recopied === 1 ? "" : "s"} and re-classified{" "}
              {result.events_reclassified} event
              {result.events_reclassified === 1 ? "" : "s"}.
            </div>
            {result.events_skipped_confirmed > 0 && (
              <div>
                {result.events_skipped_confirmed} kept a human label — photo replaced, label
                untouched.
              </div>
            )}
            {result.events_skipped_discarded > 0 && (
              <div>
                {result.events_skipped_discarded} were discarded on the old platform — photo
                replaced, discard kept.
              </div>
            )}
            {result.events_skipped_out_of_scope > 0 && (
              <div>
                {result.events_skipped_out_of_scope} not re-classified: no enabled classification
                config on this site, or outside the scope it covers.
              </div>
            )}
            {result.photos_still_uploading > 0 && (
              <div>
                {result.photos_still_uploading} still uploading — run this again once the device has
                finished.
              </div>
            )}
            {result.photos_failed > 0 && (
              <div className="text-amber-800">
                {result.photos_failed} could not be repaired — see the backend log for which.
              </div>
            )}
          </div>
        )}

        {submitError && <p className="text-xs font-mono text-destructive">{submitError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button
              onClick={() => repairMutation.mutate()}
              // The repair overwrites objects and spends Claude budget, so it
              // stays unavailable until the census says there is something to
              // repair — unlike POS2-960's food-map Apply, which is safe to run
              // on counts that timed out.
              disabled={repairMutation.isPending || ready === 0}
            >
              {repairMutation.isPending
                ? "Repairing…"
                : ready === 0
                  ? "Repair"
                  : `Repair ${ready} photo${ready === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The census read out.
 *
 * Three "nothing to do here" answers, kept apart because they mean different
 * things. No append-blob device means this site can never have a truncated
 * photo and never needs looking at again. Nothing truncated means it is clean
 * today. And a photo whose source is *itself* permanently partial — an upload
 * the device abandoned — can never be repaired by anyone, so it is reported
 * rather than counted as work.
 *
 * Photos are R2 objects and events are rows; one object can back several, so
 * the two are never added together.
 */
function CensusSummary({ census, probed }: { census: TruncatedImagesResult; probed: boolean }) {
  if (probed && census.append_blob_devices.length === 0) {
    return <div>No device on this site uploads in chunks, so no photo can be truncated.</div>;
  }
  const devices = census.append_blob_devices.join(", ");
  if (census.photos_truncated === 0) {
    return (
      <div>
        No truncated photos in {census.photos_scanned.toLocaleString()} checked on {devices}.
      </div>
    );
  }
  return (
    <>
      <div>
        <span className="font-medium">{census.photos_truncated}</span> of{" "}
        {census.photos_scanned.toLocaleString()} photos truncated on {devices}, affecting{" "}
        {census.events_affected} event{census.events_affected === 1 ? "" : "s"}.
      </div>
      <div className="text-muted-foreground">
        {census.photos_ready} complete on Azure and ready
        {census.photos_still_uploading > 0 && `, ${census.photos_still_uploading} still uploading`}
        {census.photos_source_also_truncated > 0 &&
          `, ${census.photos_source_also_truncated} truncated at the source too, nothing to repair`}
        .
      </div>
      {census.photos_ready > 0 && (
        <div className="text-muted-foreground">
          Of those: {census.events_would_reclassify} event
          {census.events_would_reclassify === 1 ? "" : "s"} will be re-classified
          {census.events_skipped_confirmed > 0 &&
            `, ${census.events_skipped_confirmed} keep a human label`}
          {census.events_skipped_discarded > 0 &&
            `, ${census.events_skipped_discarded} were discarded by the old platform and keep that`}
          {census.events_skipped_out_of_scope > 0 &&
            `, ${census.events_skipped_out_of_scope} are outside the classification scope`}
          .
        </div>
      )}
      {census.photos_missing > 0 && (
        <div className="text-amber-700">
          {census.photos_missing} object{census.photos_missing === 1 ? "" : "s"} R2 does not have.
        </div>
      )}
      {census.photos_failed > 0 && (
        <div className="text-amber-700">
          {census.photos_failed} truncated{" "}
          {census.photos_failed === 1 ? "photo has" : "photos have"} nothing left on Azure to copy
          from.
        </div>
      )}
    </>
  );
}

function ApplyFoodMapDialog({
  open,
  onOpenChange,
  oldSiteId,
  oldSiteName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  oldSiteId: number | null;
  oldSiteName: string | null;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [confirmLowerTiers, setConfirmLowerTiers] = useState(false);
  const [reapplyMapped, setReapplyMapped] = useState(false);
  const [result, setResult] = useState<ApplyFoodMapResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Whole-day inputs → inclusive full-day UTC bounds (same as classification).
  const startIso = startDate ? `${startDate}T00:00:00Z` : null;
  const endIso = endDate ? `${endDate}T23:59:59Z` : null;

  const post = async (dryRun: boolean): Promise<ApplyFoodMapResult> => {
    const res = await fetch(
      `${API_URL}/api/v1/admin/databridge/sites/${oldSiteId}/apply-food-map`,
      {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          start_date: startIso,
          end_date: endIso,
          confirm_lower_tiers: confirmLowerTiers,
          reapply_mapped: reapplyMapped,
          dry_run: dryRun,
        }),
      },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { detail?: string } | null;
      throw new Error(body?.detail ?? `${res.status}: ${res.statusText}`);
    }
    return ((await res.json()) as { data: ApplyFoodMapResult }).data;
  };

  const preview = useQuery<ApplyFoodMapResult, Error>({
    queryKey: [
      "admin",
      "databridge",
      "apply-food-map-preview",
      oldSiteId,
      startIso,
      endIso,
      confirmLowerTiers,
      reapplyMapped,
    ],
    queryFn: () => post(true),
    enabled: open && oldSiteId !== null && result === null,
    retry: false,
  });

  const applyMutation = useMutation<ApplyFoodMapResult, Error, void>({
    mutationFn: () => post(false),
    onSuccess: (data) => {
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ["admin", "databridge", "all-statuses"] });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "databridge", "site", oldSiteId, "status"],
      });
    },
    onError: (err) => setSubmitError(err.message),
  });

  useEffect(() => {
    if (!open) {
      setStartDate("");
      setEndDate("");
      setConfirmLowerTiers(false);
      setReapplyMapped(false);
      setResult(null);
      setSubmitError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Copy Old Classifications</DialogTitle>
          <DialogDescription>
            {oldSiteName ?? "Select a site"} → classify already-imported events from the old-system
            food labels (mapped foods skip the AI). Leave dates empty to cover all unclassified
            events.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          {!result && (
            <div className="grid grid-cols-2 gap-2">
              <label htmlFor="apply-map-from" className="text-xs">
                From
                <Input
                  id="apply-map-from"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label htmlFor="apply-map-to" className="text-xs">
                To
                <Input
                  id="apply-map-to"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
            </div>
          )}

          {/* POS2-699 (Problem 5): re-apply changes WHICH events are targeted —
              keep it visually distinct from the confirm sub-option (which changes
              HOW they're confirmed) so the two aren't conflated. */}
          {!result && (
            <div className="space-y-1.5 border-t pt-2">
              <div className="flex items-center gap-1">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Which events
                </p>
                <InfoHint text="Off = classify unclassified events from the old labels. On = re-copy the current map over events the food-map already classified (picking up map and confirm changes). Skips AI/manual classifications and anything a chef has confirmed or edited." />
              </div>
              <label htmlFor="apply-map-reapply" className="flex items-start gap-2 text-xs">
                <Checkbox
                  id="apply-map-reapply"
                  checked={reapplyMapped}
                  onCheckedChange={(checked) => setReapplyMapped(checked === true)}
                  className="mt-0.5"
                />
                <span>Re-apply to already-mapped events</span>
              </label>
            </div>
          )}

          {!result && (
            <div className="space-y-1.5 border-t pt-2">
              <div className="flex items-center gap-1">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Old-system classifications
                </p>
                <InfoHint text="On = copy old labels and skip the AI. Confirmed as truth only for high-confidence specific-item (L4) matches; category-level and medium/low matches appear as unconfirmed suggestions a chef can correct." />
              </div>
              <label htmlFor="apply-map-confirm-lower" className="flex items-start gap-2 text-xs">
                <Checkbox
                  id="apply-map-confirm-lower"
                  checked={confirmLowerTiers}
                  onCheckedChange={(checked) => setConfirmLowerTiers(checked === true)}
                  className="mt-0.5"
                />
                <span>Also confirm category-level (L1–L3) matches</span>
              </label>
            </div>
          )}

          {!result && preview.isLoading && <Skeleton className="h-12 w-full" />}
          {!result && preview.isError && (
            <p className="text-xs text-muted-foreground">
              Counts unavailable (large site) — you can still copy old classifications.
            </p>
          )}
          {!result && preview.data && (
            <div className="space-y-1 rounded border bg-muted/30 p-3 text-xs">
              <div>
                <span className="font-medium">{preview.data.mapped}</span> of{" "}
                {preview.data.candidates}{" "}
                {reapplyMapped
                  ? "already-mapped events will be re-copied from the current map."
                  : "unclassified events in scope will be classified from the old labels."}
              </div>
              <div className="text-muted-foreground">
                {preview.data.confirmed} confirmed · {preview.data.suggested} unconfirmed
                {Object.keys(preview.data.by_confidence).length > 0 && (
                  <>
                    {" · by confidence: "}
                    {Object.entries(preview.data.by_confidence)
                      .map(([level, n]) => `${level} ${n}`)
                      .join(", ")}
                  </>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className="rounded border bg-emerald-50 p-3 text-xs text-emerald-900">
              {result.scheduled ? (
                preview.data ? (
                  <>
                    {reapplyMapped ? "Re-applying to ~" : "Applying to ~"}
                    {preview.data.mapped} event
                    {preview.data.mapped === 1 ? "" : "s"} in the background — they will update
                    shortly. You can close this dialog.
                  </>
                ) : (
                  <>
                    {reapplyMapped ? "Re-applying" : "Applying"} in the background — events will
                    update shortly. You can close this dialog.
                  </>
                )
              ) : (
                <>
                  Classified <span className="font-medium">{result.mapped}</span> of{" "}
                  {result.candidates} events. The rest stay for the AI classifier.
                </>
              )}
            </div>
          )}

          {submitError && <p className="text-xs font-mono text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button
              onClick={() => applyMutation.mutate()}
              // POS2-960: preview counts time out on large sites; the background
              // apply recomputes server-side, so keep Apply usable when counts are
              // unavailable. Only block on a known-empty result.
              disabled={applyMutation.isPending || (!!preview.data && preview.data.mapped === 0)}
            >
              {applyMutation.isPending ? "Applying…" : "Apply"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
